<template>
	<Loader v-if="loading" />
	<div class="xpage-content-page">
		<Page>
			<div class="xpage-breadcrumbs">
				<router-link to="/" class="icon-home"></router-link>
				<span>></span>
				<router-link :to="`/batches`">{{ pageName }}</router-link>
			</div>
			<div class="xpage-inner">
				<div class="title-page">
					<h2>{{ pageName }}</h2>
				</div>
				<div class="xpage-searchbar">
					<div class="xsearchbox">
						<div class="xsearchbox-field">
							<input
								type="text"
								v-model="searchQuery"
								@mouseover="showTooltip = true"
								@mouseout="showTooltip = false"
								@keyup.enter="disbursementSearch($event)"
								:placeholder="`Search by CLUID, Name or Ref ID...`"
							/>
							<span class="tooltip-text" v-show="showTooltip">
								Enter exact to search by CLUID, Name or Ref ID...
							</span>
							<i class="icon-cancel" @click="clearSearchQuery"></i>
						</div>
						<div class="xsearchbox-submit">
							<button @click="disbursementSearch" class="icon-search">
								<span>Search</span>
							</button>
						</div>
						<div class="xpage-settings"></div>
					</div>
					<div class="page-size-selector">
						<select v-model="pageSize" @change="changePageSize">
							<option value="30">30</option>
							<option value="60">60</option>
							<option value="90">90</option>
						</select>
					</div>
				</div>
				<!-- Search Filters -->
				<div style="display: flex">
					<div class="xpage-search-filters">
						<div class="xpage-search-filter-header">
							<div class="xpage-search-filter-title">
								<h3>Search Filters</h3>
							</div>
							<div></div>
						</div>
						<div class="xpage-search-filter-content">
							<div class="xpage-search-filter-section">
								<div class="xpage-search-filter-section-title">
									<h3>Disbursement Status</h3>
								</div>
								<div class="xpage-search-filter-section-content">
									<label class="xcheck">
										Approved
										<input
											type="checkbox"
											id="approved"
											v-model="approvedCheckbox"
										/>
										<span></span>
									</label>
									<label class="xcheck">
										Pending
										<input
											type="checkbox"
											id="pending"
											v-model="pendingCheckbox"
										/>
										<span></span>
									</label>
								</div>
							</div>
							<div class="xpage-search-filter-section">
								<div class="xpage-search-filter-section-title">
									<h3>
										Batch Type
									</h3>
								</div>
								<div class="xpage-search-filter-section-content">
									<label class="xradio">
										LOC
										<input 
											type="radio" 
											id="batch-type-loc" 
											:checked="batchType === DisbursementBatchType.LOAN" 
											@change="setBatchType(DisbursementBatchType.LOAN)" />
										<span></span>
									</label>
									<label class="xradio">
										Refi
										<input 
											type="radio" 
											id="batch-type-refi" 
											:checked="batchType === DisbursementBatchType.REFI"
											@change="setBatchType(DisbursementBatchType.REFI)" />
										<span></span>
									</label>
									<label class="xradio">
										PEL
										<input 
											type="radio" 
											id="batch-type-pel" 
											:checked="batchType === DisbursementBatchType.PEL" 
											@change="setBatchType(DisbursementBatchType.PEL)" />
										<span></span>
									</label>
								</div>
							</div>
							<!-- Sort By Credit Union -->
							<div class="xpage-search-filter-section">
								<h3>Credit Union</h3>
								<input
									type="text"
									placeholder="Search by Credit Union"
									v-model="searchTerm"
								/>
								<div class="xpage-search-filter-section-content">
									<div
										v-for="(
											creditUnion, index
										) in displayCreditUnions.value.slice(0, 8)"
										:key="index"
									>
										<CircleLoader v-if="searchLoading" />
										<label class="xcheck">
											{{ creditUnion.name }}
											<input
												type="checkbox"
												:id="`checkbox-${index}`"
												:value="creditUnion"
												v-model="selectedCreditUnions"
											/>
											<span></span>
										</label>
									</div>
								</div>
								<div
									v-if="filteredCreditUnions.length > 8"
									class="xseeall"
									@click="isModalVisible = !isModalVisible"
								>
									See all
								</div>
								<!-- Modal -->
								<div v-if="isModalVisible" class="xmodal-wrapper">
									<div class="xmodal-inner">
										<div class="xmodal">
											<div
												class="xmodal-close"
												@click="closeModalAndClearSearch"
											>
												X
											</div>
											<h2>Credit Union</h2>
											<input
												class="xmodal-search"
												type="text"
												placeholder="Search by Credit Union"
												v-model="searchTerm"
											/>
											<div class="inner-modal">
												<div
													v-for="(creditUnion, index) in filteredCreditUnions"
													:key="index"
												>
													<div class="inner-modal">
														<label class="xcheck">
															{{ creditUnion.name }}
															<input
																type="checkbox"
																:id="`checkbox-${index}`"
																:value="creditUnion"
																v-model="selectedCreditUnions"
															/>
															<span></span>
														</label>
													</div>
												</div>
											</div>
											<div class="xmodal-buttons">
												<button @click="isModalVisible = false">
													Show {{ totalCount }} Results
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div v-if="filteredSearchResults.length" class="table-data">
						<div v-if="totalCount > 0" class="results-info">
							Found {{ totalCount }} results
							<span class="results-info-pages">
								Page {{ currentPage }} of {{ totalPageCountText }}
							</span>
							<div v-if="totalPages > 1" class="pagination">
								<div class="pagination-controls">
									<button
										@click="changePage(currentPage - 1)"
										:disabled="currentPage <= 1"
									>
										&lt;
									</button>
									<button
										v-for="n in totalPages"
										:key="n"
										:class="{ xclicked: n === currentPage }"
										@click="changePage(n)"
									>
										{{ n }}
									</button>
									<button
										@click="changePage(currentPage + 1)"
										:disabled="currentPage >= totalPages"
									>
										&gt;
									</button>
								</div>
							</div>
						</div>
						<PermissionsWrapper
							:disabled="!canEdit"
							:showDisabledIfNotVisible="true"
							:permissionIds="[Permissions.DisbursementBatches]"
							:permissionLevel="PermissionLevels.Read"
						>
							<table id="fifthTable">
								<thead>
									<tr>
										<th
											v-for="header in columnHeaders"
											:key="header.key"
											@click="sortTable(header.key)"
											:class="{ 'selected-column': header.key === sortColumn }"
										>
											<div class="header-container">
												<div class="header-text">{{ header.name }}</div>
												<div class="caret-container">
													<span v-if="sorting.column === header.key">
														{{ sorting.direction === "asc" ? "▲" : "▼" }}
													</span>
												</div>
											</div>
										</th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="row in filteredSearchResults"
										:key="row.id"
										@click="editDisbursementBatch(row.id, row.batchType)"
										class="clickable-row"
									>
										<td v-for="col in displayedColumns" :key="col.key">
											{{ row[col.key] }}
										</td>
									</tr>
								</tbody>
							</table>
						</PermissionsWrapper>
					</div>
					<div v-if="filteredSearchResults.length === 0" class="xfoundnothing">
						<h2 class="header">No matches found</h2>
						<p>
							<b>0 results</b>
							<span v-if="searchQuery">
								for "
								<b>{{ searchQuery }}</b>
								"
							</span>
							<span
								v-if="approved !== null || creditUnionFilterEngaged"
								class="hasFiltersApplied"
							>
								with the filter
								<b>{{ filterDescription }}</b>
							</span>
						</p>
					</div>
				</div>
			</div>
		</Page>
	</div>
</template>

<script setup lang="ts">
import { onMounted, ref, Ref, watch, computed, reactive } from "vue";
import Loader from "@/components/Loader.vue";
import Page from "@/components/layout/Page.vue";
import { Permissions, PermissionLevels } from "@/models/permissions";
import PermissionsWrapper from "@/components/wrappers/PermissionsWrapper.vue";
import { useApplicantsStore } from "@/store/store/applicantsStore";
import { useRouter } from "vue-router";
import axios from "axios";
import { useBatchesStore } from "@/store/store/batchesStore";
import CircleLoader from "@/components/shared/CircleLoader.vue";
import { DisbursementBatchType } from "@/types/disbursements";

interface SearchResultItem {
	id: number;
	batchNumber: any;
	batchType: number;
	batchTypeDescription: string;
	batchDateFormatted: string;
	approvedDateFormatted: string | null;
	statusHumanized: string;
	approved: boolean;
}

const { loadData } = useInitialData();
const router = useRouter();
const batchesStore = useBatchesStore();
const loading = ref(false);
const searchLoading = ref(false);

const canEdit = useApplicantsStore();
const isModalVisible = ref(false);

const pageName = "Disbursement Batches";
const searchFilter = ref("");
const searchTerm = ref("");

const approvedCheckbox = ref(false);
const pendingCheckbox = ref(false);
const batchType = ref(DisbursementBatchType.LOAN);
const showCreditUnion = reactive<Record<number, boolean>>({});

const searchResults: Ref<SearchResultItem[]> = ref([]);
const selectedCreditUnions = ref<{ id: number; name: string }[]>([]);

// const ascending = ref<boolean>(false);
const sortColumn = ref<string>("");
const showTooltip = ref(false);

const totalCount = ref(0);
const pageSize = ref(30);
const currentPage = ref(1);
const searchQuery = ref("");

const searchPreformed = ref(false);

const approved = computed(() => {
	if (approvedCheckbox.value && pendingCheckbox.value) {
		return null;
	} else if (approvedCheckbox.value) {
		return true;
	} else if (pendingCheckbox.value) {
		return false;
	}
	return null;
});

const lenderIds = computed(() => {
	return selectedCreditUnions.value.map((cu) => cu.id);
});

const displayCreditUnions = computed(() => {
	return selectedCreditUnions.value.length > 0
		? selectedCreditUnions
		: filteredCreditUnions;
});

const creditUnionFilterEngaged = computed(() => {
	return lenderIds.value.length > 0;
});

const filterDescription = computed(() => {
	let descriptions: string[] = [];
	if (approved.value === true) {
		descriptions.push("Approved");
	} else if (approved.value === false) {
		descriptions.push("Pending");
	}
	if (creditUnionFilterEngaged.value) {
		descriptions.push(
			`${lenderIds.value.length} Credit Union${
				lenderIds.value.length > 1 ? "s" : ""
			}`
		);
	}
	return descriptions.length ? `${descriptions.join(", ")}` : "";
});

const creditUnions = computed(() =>
	batchesStore.lenders.map((union) => {
		return {
			id: union.id,
			name: union.name,
		};
	})
);

const editDisbursementBatch = (id: number, batchType: number) => {
	// this will be refactored once loan svc is migrated to cert/disb
	// batch type will be used as product type in disb svc req
	
	if (batchType === 1) { // loc
		router.push({ name: "BatchEdit", params: { batchNumber: id } });
	} else if (batchType == 2) {
		router.push({ name: "PayoffBatchDetails", params: { batchId: id } });
	} else if (batchType == 3) {
		router.push({ name: "DisbursementBatchDetails", params: { batchId: id } });
	} else {
		alert(`Batch details are not yet implemented for type ${batchType}.`);
	}
};

const filteredCreditUnions = computed(() => {
	const filtered = creditUnions.value.filter(
		(creditUnion) =>
			!searchTerm.value ||
			creditUnion.name.toLowerCase().includes(searchTerm.value.toLowerCase())
	);

	filtered.sort((a, b) => a.name.localeCompare(b.name));
	return filtered;
});

const totalPages = computed(() => {
	if (pageSize.value > 0) {
		return Math.ceil(totalCount.value / pageSize.value);
	} else {
		return 1;
	}
});

const searchParams = computed(() => ({
	search: searchQuery.value.trim() || null,
	batchType: batchType.value,
	approved: approved.value !== null ? approved.value : null,
	lenderIds: lenderIds.value.length > 0 ? lenderIds.value : undefined,
	createdSince: null,
	page: {
		index: currentPage.value,
		size: pageSize.value,
	},
}));

function createColumnHeaders(columns: { key: string; name: string }[]) {
	return columns.map((col) => ({
		key: col.key,
		name: col.name,
	}));
}

const displayedColumns = ref([
	{ key: "batchNumber", name: "Batch Number" },
	{ key: "batchTypeDescription", name: "Batch Type" },
	{ key: "batchDateFormatted", name: "Batch Date" },
	{ key: "approvedDateFormatted", name: "Approved Date" },
	{ key: "status", name: "Status" },
]);

const columnHeaders = computed(() =>
	createColumnHeaders(displayedColumns.value)
);

const setBatchType = (type: DisbursementBatchType) => batchType.value = type;

const closeModalAndClearSearch = () => {
	isModalVisible.value = false;
};

const clearSearchQuery = () => {
	searchQuery.value = "";
	searchPreformed.value = false;
	disbursementSearch(true);
};

watch(pageSize, () => {
	currentPage.value = 1;
});

const changePageSize = () => {
	disbursementSearch();
};

const resetCheckboxes = () => {
	creditUnions.value.forEach((creditUnion) => {
		showCreditUnion[creditUnion.id] = false;
	});
};

const totalPageCountText = computed(() => {
	return `${totalPages.value} ${totalPages.value === 1 ? "page" : "pages"}`;
});

const disbursementSearch = async (
	event?: MouseEvent | KeyboardEvent | boolean
) => {
	let resetPage = false;

	if (typeof event === "boolean") {
		resetPage = event;
	}

	if (resetPage) {
		currentPage.value = 1;
	}

	loading.value = true;
	searchPreformed.value = true;

	try {
		const response = await axios.post(
			"/api/disbursementbatches/search",
			searchParams.value
		);
		totalCount.value = response.data.totalCount;
		searchResults.value = response.data.items;
	} catch (error) {
		console.error(error);
	} finally {
		loading.value = false;
	}
};

// Sorting
const sorting = reactive({
	column: null,
	direction: "asc",
});

function sortTable(column) {
	if (sorting.column === column) {
		sorting.direction = sorting.direction === "asc" ? "desc" : "asc";
	} else {
		sorting.column = column;
		sorting.direction = "asc";
	}
	searchResults.value.sort((a, b) => {
		let aValue = a[column],
			bValue = b[column];

		if (aValue === null && bValue === null) {
			return 0;
		} else if (aValue === null) {
			return sorting.direction === "asc" ? -1 : 1;
		} else if (bValue === null) {
			return sorting.direction === "asc" ? 1 : -1;
		}

		if (!isNaN(Date.parse(aValue)) && !isNaN(Date.parse(bValue))) {
			aValue = new Date(aValue);
			bValue = new Date(bValue);
		}

		if (aValue < bValue) {
			return sorting.direction === "asc" ? -1 : 1;
		} else if (aValue > bValue) {
			return sorting.direction === "asc" ? 1 : -1;
		} else {
			return 0;
		}
	});
}

function changePage(page: number) {
	currentPage.value = page;
}

const filteredSearchResults = computed(() => {
	if (approved.value === null) {
		return searchResults.value;
	} else if (approved.value) {
		return searchResults.value.filter((item) => item.approved === true);
	} else {
		return searchResults.value.filter((item) => item.approved === false);
	}
});

const pagedSearchResults = async () => {
	const searchParams = {
		search: null,
		batchStatus: 0,
		lenderIds: [],
		createdSince: null,
		page: {
			index: 1,
			size: 30,
		},
	};
	const response = await axios.post(
		"/api/disbursementbatches/search",
		searchParams
	);
	searchFilter.value = response.data.items;
};

creditUnions.value.forEach((creditUnion) => {
	showCreditUnion[creditUnion.id] = false;
});

watch(
	searchParams,
	() => {
		disbursementSearch();
	},
	{ deep: true }
);

watch(searchTerm, () => {
	resetCheckboxes();
	disbursementSearch();
});

function useInitialData() {
	const loadData = async () => {
		try {
			await Promise.all([
				disbursementSearch(),
				batchesStore.loadLenders(),
			]);
			processLenderData();
		} catch (error) {
			console.error("Failed To load initial Data:", error);
		}
	};

	const processLenderData = () => {
		creditUnions.value.forEach((creditUnion) => {
			showCreditUnion[creditUnion.id] = false;
		});
	};
	return { loadData, processLenderData };
}

onMounted(async () => {
	await loadData();
});
</script>

<style scoped>
.header {
	padding-top: 5px;
	margin-bottom: 10px;
}
.title-page {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

table {
	width: 100%;
	border-collapse: collapse;
	border-spacing: 0;
	border-radius: 10px;
	overflow: hidden;
}

thead {
	background-color: #f8f8f8;
	text-transform: capitalize;
}

thead th:first-child {
	border-top-left-radius: 10px;
}

thead th:last-child {
	border-top-right-radius: 10px;
}

tbody tr:last-child td:first-child {
	border-bottom-left-radius: 10px;
}

tbody tr:last-child td:last-child {
	border-bottom-right-radius: 10px;
}

th {
	cursor: pointer;
	width: 25%;
	padding: 20px;
}

th,
td {
	padding: 8px;
	border: 1px solid #ddd;
	text-align: center;
	font-weight: 600;
}

tr:nth-child(even) {
	background-color: #f2f2f2;
}

table th:hover {
	background: #000000;
	color: white;
}

div.xpage-inner {
	padding-right: 0;
}
.results-info {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 10px;
	margin-bottom: 10px;
	line-height: 44px;
	margin-right: 10px;
	font-family: Montserrat, arial, sans-serif;
	font-size: 19px;
	font-weight: 800;
	border-bottom: 1px solid rgb(183, 183, 183);
}

.results-info-pages {
	line-height: 20px;
	text-transform: uppercase;
	font-size: 12px;
	font-weight: 700;
	margin-left: 10px;
}

input:hover + .tooltip-text {
	visibility: visible;
	opacity: 1;
}
.caret {
	position: right;
	float: right;
	width: 12px;
	height: 15px;
	background-repeat: no-repeat;
	background-size: contain;
	background-position-y: bottom;
}

.xpage-content-page {
	padding-left: 20px;
}

.pagination-controls {
	display: inline-block;
}

.pagination button:disabled {
	border-color: #eaeaea;
	cursor: not-allowed;
	color: #ccc;
}

.pagination .xclicked {
	background-color: #000;
	color: #fff;
}

.pagination span {
	padding: 5px 10px;
}

.pagination ellipsis {
	padding: 5px 10px;
}

.page-size-selector select {
	padding: 5px 10px;
	font-size: 14px;
}

.page-size-selector {
	margin-left: 10px;
	display: inline-block;
	margin-right: 10px;
}

.xpage-searchbar {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding-bottom: 20px;
}

.tooltip-text {
	position: absolute;
	top: 100%;
	left: 10%;
	transform: translateX(-50%);
	background-color: #333;
	color: #fff;
	padding: 5px;
	border-radius: 3px;
	font-size: 12px;
	white-space: nowrap;
	visibility: hidden;
	opacity: 0;
	transition: visibility 0s, opacity 0.3s linear;
}

.header-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.header-text {
	flex-grow: 1;
	text-align: center;
}

.caret-container {
	width: 20px;
	height: 20px;
	display: flex;
	justify-content: center;
	align-items: center;
	padding-left: 10px;
	padding-right: 10px;
}

.selected-column {
	background-color: #d4d6dd;
}

.clickable-row {
	cursor: pointer;
}

.clickable-row:hover {
	background-color: #d4d6dd;
}

.table-data {
	width: 100%;
	font-family: Montserrat, arial, sans-serif;
	margin: 20px;
	padding: 0 10px;
}

.xseeall {
	padding-top: 10px;
}

.xpage-search-filters {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	background-color: #f8f8f8;
	padding: 20px;
	width: 290px;
	border-radius: 10px;
}

.inner-modal {
	text-align: left;
	max-height: 300px;
	overflow-y: auto;
}

.xfoundnothing {
	width: 100%;
	font-family: Montserrat, arial, sans-serif;
	margin: 20px;
	padding: 0 10px;
}
</style>
